import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { IntakeForm } from '@/components/IntakeForm';
import { LeadInterestForm } from '@/components/LeadInterestForm';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const fitChecks = [
  'The work is bounded enough for a sharp diagnostic, advisory lane, prototype, or build sprint.',
  'You can describe the problem without sending private records first.',
  'You want a finance operator who can also reason about systems, AI-assisted workflows, and execution.',
  'The ask is fractional, project-based, advisory, collaborative, or referral-shaped rather than full-time employment.',
];

const outcomes = [
  {
    title: 'Fit clarity',
    body: 'A direct read on whether the next step should be a diagnostic, advisory call, build sprint, collaboration, or referral.',
  },
  {
    title: 'Systems judgment',
    body: 'A practical view of where finance, tooling, automation, and human review should meet.',
  },
  {
    title: 'Clean handoff',
    body: 'A lightweight note that gives enough context to respond without collecting private records too early.',
  },
];

const proof = [
  {
    result: 'Faster close',
    title: 'Close work made usable',
    body: 'Reworked close support around structured trackers, accrual logic, reconciliation controls, and review rhythm.',
  },
  {
    result: 'Finance + tooling',
    title: 'Operator who builds',
    body: 'Delivered accounting operations while also shaping reporting flows, models, and internal tools around the work.',
  },
  {
    result: 'Bounded proof',
    title: 'No private records in public',
    body: 'Proof is intentionally anonymized: no screenshots, raw financial data, client names, or confidential source files.',
  },
];

const process = [
  {
    step: '01',
    title: 'Interest note',
    body: 'Send the async prefit below. No files, exports, screenshots, credentials, or private values.',
  },
  {
    step: '02',
    title: 'Optional priority layer',
    body: 'If the signal is real, add the six-question priority note so I can reply with sharper async context.',
  },
  {
    step: '03',
    title: 'Fit/no-fit reply',
    body: 'I respond with a direct fit callout: decline, hold, diagnostic, build sprint, private prototype, or better-fit specialist.',
  },
  {
    step: '04',
    title: 'Calendar by exception',
    body: 'If a live conversation will materially improve the scope, I send the scheduling link after the written prefit.',
  },
];

const prefitQuestions = [
  'What kind of work are you exploring: side project, advisory, AI-augmented workflow, finance diagnostic, writing, referral, or collaboration?',
  'What decision, workflow, or opportunity is currently stuck?',
  'What would make the next step useful: a diagnostic map, a build sprint, a prototype, an advisory review, or a specialist referral?',
  'What has already been tried, and what should not be touched yet?',
];

const boundaries = [
  'No raw financial data, screenshots, exports, credentials, client names, or regulated data in the first note.',
  'No tax filing, legal advice, audit sign-off, or regulatory representation.',
  'No public case-study use without explicit approval and sanitization.',
  'No full-time, always-on, or emergency finance support commitment.',
  'No quote, payment, product access, auto-reply promise, or secure-room authorization from the first note.',
];

type PriorityUnlock = {
  name?: string;
  email?: string;
  leadId?: string;
  continueToken?: string;
  continueUrl?: string;
  stage2Prefill?: Stage2Prefill;
};

type Stage2Prefill = Partial<{
  q1_shape: string;
  q2_friction: string;
  q3_fix: string;
  q4_history: string;
  q5_help: 'map' | 'build' | 'advisory' | 'unsure' | '';
  q6_boundary: 'async-first' | 'next-step' | '';
}>;

type ContinueLookupResponse = {
  ok?: boolean;
  lead_id?: string;
  contact?: {
    name?: string;
    email?: string;
    company?: string;
    role?: string;
  };
  stage2?: {
    qualification_answers?: Record<string, unknown>;
  } | null;
  error?: string;
};

export default function WorkWithMe() {
  const [priorityUnlock, setPriorityUnlock] = useState<PriorityUnlock | null>(null);
  const priorityRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const continueToken = params.get('continue')?.trim();
    if (!continueToken) return;
    setPriorityUnlock({ continueToken });
  }, []);

  useEffect(() => {
    const continueToken = priorityUnlock?.continueToken;
    if (!continueToken || priorityUnlock.leadId || priorityUnlock.email) return;

    let cancelled = false;
    fetch(`/api/lead-inquiry?continue=${encodeURIComponent(continueToken)}`)
      .then((response) => response.json() as Promise<ContinueLookupResponse>)
      .then((data) => {
        if (cancelled || !data.ok) return;
        setPriorityUnlock({
          continueToken,
          leadId: data.lead_id,
          name: textOrUndefined(data.contact?.name),
          email: textOrUndefined(data.contact?.email),
          stage2Prefill: jlpStage2PrefillFromLookup(data.stage2?.qualification_answers),
        });
      })
      .catch(() => {
        // Keep the unlock usable even if the readback fails; the POST token
        // remains the authority for appending to the original row.
      });

    return () => {
      cancelled = true;
    };
  }, [priorityUnlock]);

  useEffect(() => {
    if (!priorityUnlock?.continueToken && !priorityUnlock?.email) return;
    window.requestAnimationFrame(() => {
      priorityRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [priorityUnlock]);

  function unlockPriorityLayer(submission: PriorityUnlock) {
    setPriorityUnlock(submission);
  }

  return (
    <div className="overflow-x-hidden bg-paper">
      <section id="work-start" data-guide-label="Start" className="border-b border-ink/5">
        <div className="mx-0 grid max-w-[390px] gap-12 px-6 py-20 sm:mx-auto sm:max-w-5xl md:grid-cols-[1.05fr_0.95fr] md:items-center md:py-28">
          <div className="min-w-0">
            <h1 className="font-display text-3xl leading-[1.05] tracking-tight sm:text-4xl md:text-6xl">
              Finance, systems, and AI-augmented work without a full-time hire.
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-ink/70">
              I help founders, operators, and collaborators turn ambiguous
              finance or workflow problems into clearer decisions. Start with a
              public-safe written note; use live time only when the fit is real.
            </p>
            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg" className="w-full sm:w-auto">
                <a href="#prefit">Start async prefit</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="w-full sm:w-auto">
                <Link to="/proof">Review proof first</Link>
              </Button>
            </div>
          </div>

          <div className="min-w-0 border-l-2 border-accent pl-8">
            <p className="font-display text-5xl leading-none text-accent md:text-6xl">
              Map.
              <br />
              Decide.
              <br />
              Then build.
            </p>
            <p className="mt-6 text-ink/70">
              Side projects, advisory, diagnostics, prototypes, and selective
              collaboration.
            </p>
          </div>
        </div>
      </section>

      <section id="fit" data-guide-label="Best fit" className="border-b border-ink/5">
        <div className="mx-0 max-w-[390px] px-6 py-20 sm:mx-auto sm:max-w-5xl">
          <div className="grid gap-10 md:grid-cols-[0.9fr_1.1fr]">
            <div>
              <p className="font-display text-xs uppercase tracking-[0.25em] text-accent">
                Best fit
              </p>
              <h2 className="mt-3 font-display text-3xl tracking-tight md:text-4xl">
                This is for useful work that needs shape before commitment.
              </h2>
            </div>
            <ul className="grid gap-4 text-ink/75">
              {fitChecks.map((item) => (
                <li key={item} className="flex gap-3 border-b border-ink/10 pb-4">
                  <span className="mt-1 text-accent">→</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section id="outcomes" data-guide-label="Outcomes" className="border-b border-ink/5">
        <div className="mx-0 max-w-[390px] px-6 py-20 sm:mx-auto sm:max-w-5xl">
          <p className="font-display text-xs uppercase tracking-[0.25em] text-accent">
            What the map clarifies
          </p>
          <h2 className="mt-3 max-w-3xl font-display text-3xl tracking-tight md:text-4xl">
            The first job is to find the right next move, not force a sales call.
          </h2>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {outcomes.map((outcome) => (
              <Card key={outcome.title}>
                <CardContent className="p-8">
                  <h3 className="font-display text-xl">{outcome.title}</h3>
                  <p className="mt-4 text-ink/70">{outcome.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section id="proof" data-guide-label="Proof" className="border-b border-ink/5 bg-ink text-paper">
        <div className="mx-0 max-w-[390px] px-6 py-20 sm:mx-auto sm:max-w-5xl">
          <p className="font-display text-xs uppercase tracking-[0.25em] text-accent">
            Proof of work
          </p>
          <h2 className="mt-3 max-w-3xl font-display text-3xl tracking-tight md:text-4xl">
            Prior work, anonymized. Useful enough to inspect, restrained enough to publish.
          </h2>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {proof.map((item) => (
              <article key={item.title} className="border-l-2 border-accent pl-6">
                <p className="font-display text-3xl text-accent">{item.result}</p>
                <h3 className="mt-4 font-display text-xl">{item.title}</h3>
                <p className="mt-4 text-paper/70">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="process" data-guide-label="Process" className="border-b border-ink/5">
        <div className="mx-0 max-w-[390px] px-6 py-20 sm:mx-auto sm:max-w-5xl">
          <p className="font-display text-xs uppercase tracking-[0.25em] text-accent">
            How we start
          </p>
          <h2 className="mt-3 max-w-3xl font-display text-3xl tracking-tight md:text-4xl">
            The funnel protects attention: written signal first, live time only when useful.
          </h2>
          <div className="mt-12 grid gap-8 md:grid-cols-4">
            {process.map((item) => (
              <div key={item.step}>
                <p className="font-display text-5xl text-accent">{item.step}</p>
                <h3 className="mt-4 font-display text-xl">{item.title}</h3>
                <p className="mt-3 text-ink/70">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="prefit-context" data-guide-label="Context" className="border-b border-ink/5 bg-paper">
        <div className="mx-0 grid max-w-[390px] gap-10 px-6 py-20 sm:mx-auto sm:max-w-5xl md:grid-cols-[0.85fr_1.15fr] md:items-start">
          <div>
            <p className="font-display text-xs uppercase tracking-[0.25em] text-accent">
              Before the form
            </p>
            <h2 className="mt-3 font-display text-3xl tracking-tight md:text-4xl">
              Useful context, no private records.
            </h2>
            <p className="mt-4 text-ink/70">
              Keep it lightweight. The goal is to understand the shape of the
              work and decide the safest next step.
            </p>
            <div className="mt-8">
              <Button asChild size="lg">
                <a href="#prefit">Open async prefit</a>
              </Button>
            </div>
          </div>
          <ol className="grid gap-4 text-ink/75">
            {prefitQuestions.map((item, index) => (
              <li key={item} className="flex gap-4 border-b border-ink/10 pb-4">
                <span className="font-display text-accent">{String(index + 1).padStart(2, '0')}</span>
                <span>{item}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section id="prefit" data-guide-label="Prefit" className="border-b border-ink/5 bg-paper">
        <div className="mx-0 grid max-w-[390px] gap-10 px-6 py-20 sm:mx-auto sm:max-w-5xl md:grid-cols-[0.82fr_1.18fr] md:items-start">
          <div>
            <p className="font-display text-xs uppercase tracking-[0.25em] text-accent">
              Async prefit
            </p>
            <h2 className="mt-3 font-display text-3xl tracking-tight md:text-4xl">
              Start with the original fit interview.
            </h2>
            <p className="mt-4 text-ink/70">
              This is the primary intake. It routes the shape, timing, role,
              and likely next step before any private records or live calendar
              time enter scope.
            </p>
          </div>
          <LeadInterestForm onSuccess={unlockPriorityLayer} />
        </div>

        {priorityUnlock && (
          <div
            id="priority-prefit"
            data-guide-label="Stage 2"
            ref={priorityRef}
            className="mx-0 grid max-w-[390px] gap-10 px-6 pb-20 sm:mx-auto sm:max-w-5xl md:grid-cols-[0.82fr_1.18fr] md:items-start"
          >
            <div>
              <p className="font-display text-xs uppercase tracking-[0.25em] text-accent">
                Stage 2 unlocked
              </p>
              <h2 className="mt-3 font-display text-3xl tracking-tight md:text-4xl">
                Optional priority note.
              </h2>
              <p className="mt-4 text-ink/70">
                Your async prefit is already in. Add this only if you want to
                sharpen the async reply and make priority review easier. Same
                public-safe boundary: send the shape, not the private data.
              </p>
            </div>
            <IntakeForm
              key={priorityUnlock.leadId || priorityUnlock.continueToken || priorityUnlock.email || 'priority'}
              sourceRoute="/work-with-me#priority-prefit"
              sourceHash="#priority-prefit"
              extraTags={['priority-layer', 'optional-prefit']}
              prefill={priorityUnlock}
              stage2Prefill={priorityUnlock.stage2Prefill}
              contactLocked={Boolean(priorityUnlock.name && priorityUnlock.email)}
              stage2UpdateToken={priorityUnlock.continueToken}
            />
          </div>
        )}
      </section>

      <section id="scope-boundaries" data-guide-label="Boundaries" className="border-b border-ink/5">
        <div className="mx-0 grid max-w-[390px] gap-10 px-6 py-20 sm:mx-auto sm:max-w-5xl md:grid-cols-2 md:items-start">
          <div>
            <p className="font-display text-xs uppercase tracking-[0.25em] text-accent">
              Boundaries
            </p>
            <h2 className="mt-3 font-display text-3xl tracking-tight md:text-4xl">
              Clear no's make the yes safer.
            </h2>
            <p className="mt-4 text-ink/70">
              The diagnostic is designed to inspect the shape of the system
              before any sensitive material or larger commitment enters scope.
            </p>
          </div>
          <ul className="grid gap-3 text-ink/70">
            {boundaries.map((item) => (
              <li key={item} className="flex gap-3">
                <span className="text-accent">x</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section id="final-cta" data-guide-label="Start note" className="bg-paper">
        <div className="mx-0 max-w-[390px] px-6 py-20 text-center sm:mx-auto sm:max-w-5xl">
          <h2 className="mx-auto max-w-3xl font-display text-3xl tracking-tight md:text-5xl">
            If the work has a real signal, start with a clear note.
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-ink/70">
            Send the shape of the opportunity. Keep private data out until the
            scope and handling path are clear.
          </p>
          <div className="mt-10">
            <Button asChild size="lg">
              <a href="#prefit">Start async prefit</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

function jlpStage2PrefillFromLookup(value: Record<string, unknown> | undefined): Stage2Prefill | undefined {
  if (!value) return undefined;
  const prefill: Stage2Prefill = {};
  const q1 = textOrUndefined(value.stage2_q1_shape);
  const q2 = textOrUndefined(value.stage2_q2_friction);
  const q3 = textOrUndefined(value.stage2_q3_fix);
  const q4 = textOrUndefined(value.stage2_q4_history);
  const q5 = jlpHelpShape(value.stage2_q5_help);
  const q6 = jlpBoundary(value.stage2_q6_boundary);

  if (q1) prefill.q1_shape = q1;
  if (q2) prefill.q2_friction = q2;
  if (q3) prefill.q3_fix = q3;
  if (q4) prefill.q4_history = q4;
  if (q5) prefill.q5_help = q5;
  if (q6) prefill.q6_boundary = q6;

  return Object.keys(prefill).length > 0 ? prefill : undefined;
}

function textOrUndefined(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function jlpHelpShape(value: unknown): Stage2Prefill['q5_help'] | undefined {
  return value === 'map' || value === 'build' || value === 'advisory' || value === 'unsure'
    ? value
    : undefined;
}

function jlpBoundary(value: unknown): Stage2Prefill['q6_boundary'] | undefined {
  return value === 'async-first' || value === 'next-step' ? value : undefined;
}
